// src/store/thunks/appointmentThunks.js
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import {
  normalizeIntentForBackend,
  normalizeMetadataForBackend,
} from "../../utils/appointmentDataNormalizer";

// ========================================
// ADMIN: APPOINTMENT MANAGEMENT
// ========================================

/**
 * Get all appointments (admin)
 */
export const getAppointmentsThunk = createAsyncThunk(
  "appointment/admin/getAll",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin-appointments", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Create appointment for customer (admin booking)
 */
export const adminCreateAppointmentThunk = createAsyncThunk(
  "appointment/admin/create",
  async (appointmentData, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin-appointments", appointmentData);
      return response.data.appointment || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Get booking information (available slots, branch details, etc.)
 */
export const getBookingInfoThunk = createAsyncThunk(
  "appointment/admin/getBookingInfo",
  async ({ branchCode, date }, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin-appointments/book", {
        params: { branchCode, date },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Get customer's appointment history (admin view)
 */
export const getCustomerAppointmentsThunk = createAsyncThunk(
  "appointment/admin/getCustomerAppointments",
  async ({ userId, ...params }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin-appointments/customer/${userId}`, {
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Get appointment details (admin view)
 */
export const getAppointmentDetailsThunk = createAsyncThunk(
  "appointment/admin/getDetails",
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin-appointments/${appointmentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Get today's appointments
 */
export const getTodayAppointmentsThunk = createAsyncThunk(
  "appointment/admin/getToday",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin-appointments/today", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Get upcoming appointments
 */
export const getUpcomingAppointmentsThunk = createAsyncThunk(
  "appointment/admin/getUpcoming",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin-appointments/upcoming", {
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Search appointments
 */
export const searchAppointmentsThunk = createAsyncThunk(
  "appointment/admin/search",
  async (searchParams, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin-appointments/search", {
        params: searchParams,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// ========================================
// ADMIN: APPROVAL WORKFLOW
// ========================================

/**
 * Get pending approvals
 */
export const getPendingApprovalsThunk = createAsyncThunk(
  "appointment/admin/getPendingApprovals",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin-appointments/pending-approvals", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Approve appointment
 */
export const approveAppointmentThunk = createAsyncThunk(
  "appointment/admin/approve",
  async ({ appointmentId, approvalData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/approve`,
        approvalData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Reject appointment
 */
export const rejectAppointmentThunk = createAsyncThunk(
  "appointment/admin/reject",
  async ({ appointmentId, rejectionData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/reject`,
        rejectionData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Bulk approve appointments
 */
export const bulkApproveAppointmentsThunk = createAsyncThunk(
  "appointment/admin/bulkApprove",
  async (appointmentIds, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin-appointments/bulk-approve", {
        appointmentIds,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// ========================================
// 🆕 NEW: ADMIN ASSIGNMENT & AVAILABILITY
// ========================================

/**
 * Get available admins for a time slot
 */
export const getAvailableAdminsThunk = createAsyncThunk(
  "appointment/admin/getAvailableAdmins",
  async (
    { appointmentId, branchCode, scheduledDateTime, duration, appointmentType },
    { rejectWithValue }
  ) => {
    try {
      let url;
      const params = {
        branchCode,
        scheduledDateTime,
        duration,
      };

      if (appointmentType) params.appointmentType = appointmentType;

      if (appointmentId) {
        // For specific appointment (reassignment)
        params.appointmentId = appointmentId;
        url = `/admin-appointments/${appointmentId}/available-admins`;
      } else {
        // For general query
        url = "/admin-appointments/check-available-admins";
      }

      const response = await api.get(url, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Assign admin to appointment (OLD - kept for compatibility)
 */
export const assignAdminThunk = createAsyncThunk(
  "appointment/admin/assignAdmin",
  async ({ appointmentId, assignmentData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/assign-admin`,
        assignmentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Reassign admin to appointment (with availability check)
 */
export const reassignAdminThunk = createAsyncThunk(
  "appointment/admin/reassignAdmin",
  async ({ appointmentId, newAdminCode, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/reassign-admin`,
        { newAdminCode, reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Unassign admin from appointment
 */
export const unassignAdminThunk = createAsyncThunk(
  "appointment/admin/unassignAdmin",
  async ({ appointmentId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/unassign-admin`,
        { reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Auto-assign admin based on workload
 */
export const autoAssignAdminThunk = createAsyncThunk(
  "appointment/admin/autoAssignAdmin",
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/auto-assign-admin`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Get admin's schedule
 */
export const getAdminScheduleThunk = createAsyncThunk(
  "appointment/admin/getAdminSchedule",
  async ({ adminCode, startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/admin-appointments/admin-schedule/${adminCode}`,
        {
          params: { startDate, endDate },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Bulk reassign appointments
 */
export const bulkReassignAppointmentsThunk = createAsyncThunk(
  "appointment/admin/bulkReassign",
  async ({ appointmentIds, newAdminCode, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin-appointments/bulk-reassign", {
        appointmentIds,
        newAdminCode,
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// ========================================
// 🆕 NEW: APPOINTMENT FIELD UPDATES
// ========================================

/**
 * Update appointment type
 */
export const updateAppointmentTypeThunk = createAsyncThunk(
  "appointment/admin/updateType",
  async ({ appointmentId, type, reason }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-appointments/${appointmentId}/type`,
        { type, reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Update appointment duration
 */
export const updateAppointmentDurationThunk = createAsyncThunk(
  "appointment/admin/updateDuration",
  async ({ appointmentId, duration, reason }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-appointments/${appointmentId}/duration`,
        { duration, reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Update appointment intent
 */
export const updateAppointmentIntentThunk = createAsyncThunk(
  "appointment/admin/updateIntent",
  async ({ appointmentId, intent }, { rejectWithValue }) => {
    try {
      // ✅ FIX: Normalize field names before sending
      const normalizedIntent = normalizeIntentForBackend(intent);

      console.log("🔄 Normalizing intent:", {
        original: intent,
        normalized: normalizedIntent,
      });

      const response = await api.patch(
        `/admin-appointments/${appointmentId}/intent`,
        { intent: normalizedIntent } // ← Send normalized version
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Update appointment priority
 */
export const updateAppointmentPriorityThunk = createAsyncThunk(
  "appointment/admin/updatePriority",
  async ({ appointmentId, priority, reason }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-appointments/${appointmentId}/priority`,
        { priority, reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Add tags to appointment
 */
export const addAppointmentTagsThunk = createAsyncThunk(
  "appointment/admin/addTags",
  async ({ appointmentId, tags }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/tags`,
        { tags }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Remove tags from appointment
 */
export const removeAppointmentTagsThunk = createAsyncThunk(
  "appointment/admin/removeTags",
  async ({ appointmentId, tags }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/admin-appointments/${appointmentId}/tags`,
        { data: { tags } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Update appointment metadata
 */
export const updateAppointmentMetadataThunk = createAsyncThunk(
  "appointment/admin/updateMetadata",
  async ({ appointmentId, metadata }, { rejectWithValue }) => {
    try {
      const normalizedMetadata = normalizeMetadataForBackend(metadata);

      console.log("🔄 Normalizing metadata:", {
        original: metadata,
        normalized: normalizedMetadata,
      });

      const response = await api.patch(
        `/admin-appointments/${appointmentId}/metadata`,
        { metadata: normalizedMetadata } // ← Send normalized version
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Update reminder preferences
 */
export const updateReminderPreferencesThunk = createAsyncThunk(
  "appointment/admin/updateReminderPreferences",
  async ({ appointmentId, reminderPreferences }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-appointments/${appointmentId}/reminder-preferences`,
        { reminderPreferences }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Bulk update appointments
 */
export const bulkUpdateAppointmentsThunk = createAsyncThunk(
  "appointment/admin/bulkUpdate",
  async ({ appointmentIds, updates }, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin-appointments/bulk-update", {
        appointmentIds,
        updates,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// ========================================
// ADMIN: APPOINTMENT MODIFICATIONS (EXISTING)
// ========================================

/**
 * Update appointment notes (EXISTING - Enhanced)
 */
export const updateNotesThunk = createAsyncThunk(
  "appointment/admin/updateNotes",
  async ({ appointmentId, notesData }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-appointments/${appointmentId}/notes`,
        notesData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Reschedule appointment (admin-initiated)
 */
export const rescheduleAppointmentThunk = createAsyncThunk(
  "appointment/admin/reschedule",
  async ({ appointmentId, rescheduleData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/reschedule`,
        rescheduleData
      );
      return response.data;
    } catch (error) {
      console.log("Reschedule Error:", { error });
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Cancel appointment (admin-initiated)
 */
export const cancelAppointmentThunk = createAsyncThunk(
  "appointment/admin/cancel",
  async ({ appointmentId, cancellationData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/cancel`,
        cancellationData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// ========================================
// ADMIN: VISIT TRACKING
// ========================================

/**
 * Check-in customer
 */
export const checkInCustomerThunk = createAsyncThunk(
  "appointment/admin/checkIn",
  async ({ appointmentId, checkInData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/check-in`,
        checkInData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Start appointment
 */
export const startAppointmentThunk = createAsyncThunk(
  "appointment/admin/start",
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/start`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Track product viewed
 */
export const trackProductViewedThunk = createAsyncThunk(
  "appointment/admin/trackProductViewed",
  async ({ appointmentId, productData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/track-product-viewed`,
        productData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Track product tried on
 */
export const trackProductTriedOnThunk = createAsyncThunk(
  "appointment/admin/trackProductTriedOn",
  async ({ appointmentId, productData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/track-product-tried`,
        productData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Add interaction
 */
export const addInteractionThunk = createAsyncThunk(
  "appointment/admin/addInteraction",
  async ({ appointmentId, interactionData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/add-interaction`,
        interactionData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Update visit notes
 */
export const updateVisitNotesThunk = createAsyncThunk(
  "appointment/admin/updateVisitNotes",
  async ({ appointmentId, visitNotesData }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-appointments/${appointmentId}/visit-notes`,
        visitNotesData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Complete visit
 */
export const completeVisitThunk = createAsyncThunk(
  "appointment/admin/completeVisit",
  async ({ appointmentId, visitData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/complete-visit`,
        visitData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Mark as no-show
 */
export const markNoShowThunk = createAsyncThunk(
  "appointment/admin/markNoShow",
  async ({ appointmentId, noShowData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-appointments/${appointmentId}/mark-no-show`,
        noShowData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// ========================================
// ANALYTICS & REPORTING
// ========================================

/**
 * Get appointment analytics
 */
export const getAppointmentAnalyticsThunk = createAsyncThunk(
  "appointment/analytics/get",
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin-appointments/analytics", {
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Get branch metrics
 */
export const getBranchMetricsThunk = createAsyncThunk(
  "appointment/analytics/branchMetrics",
  async ({ branchCode, ...params }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/admin-appointments/branch-metrics/${branchCode}`,
        {
          params,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Get statistics summary
 */
export const getStatisticsThunk = createAsyncThunk(
  "appointment/analytics/statistics",
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin-appointments/statistics", {
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// ========================================
// 🆕 ENHANCED STATISTICS & ANALYTICS
// ========================================

/**
 * Get Dashboard Statistics - COMPREHENSIVE & ACCURATE
 */
export const getDashboardStatisticsThunk = createAsyncThunk(
  "appointment/stats/dashboard",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.branchCode)
        queryParams.append("branchCode", params.branchCode);
      if (params.startDate) queryParams.append("startDate", params.startDate);
      if (params.endDate) queryParams.append("endDate", params.endDate);

      const response = await api.get(
        `/admin-appointments/stats/dashboard?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to fetch dashboard statistics"
      );
    }
  }
);

/**
 * Get Appointment Booking Density for Calendar
 */
export const getAppointmentBookingDensityThunk = createAsyncThunk(
  "appointment/stats/bookingDensity",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.branchCode)
        queryParams.append("branchCode", params.branchCode);
      if (params.year) queryParams.append("year", params.year);
      if (params.month) queryParams.append("month", params.month);
      if (params.startDate) queryParams.append("startDate", params.startDate);
      if (params.endDate) queryParams.append("endDate", params.endDate);
      if (params.viewMode) queryParams.append("viewMode", params.viewMode);

      const response = await api.get(
        `/admin-appointments/stats/booking-density?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to fetch booking density"
      );
    }
  }
);

/**
 * Get Statistics by Status
 */
export const getStatisticsByStatusThunk = createAsyncThunk(
  "appointment/stats/byStatus",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.branchCode)
        queryParams.append("branchCode", params.branchCode);
      if (params.startDate) queryParams.append("startDate", params.startDate);
      if (params.endDate) queryParams.append("endDate", params.endDate);

      const response = await api.get(
        `/admin-appointments/stats/by-status?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to fetch status statistics"
      );
    }
  }
);

/**
 * Get appointment timeline
 */
export const getTimelineThunk = createAsyncThunk(
  "appointment/analytics/timeline",
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/admin-appointments/${appointmentId}/timeline`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Export appointments
 */
export const exportAppointmentsThunk = createAsyncThunk(
  "appointment/analytics/export",
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin-appointments/export", {
        params,
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// ========================================
// SLOT AVAILABILITY
// ========================================

const getAvailableSlots = async ({ branchCode, date, duration }) => {
  const response = await api.get(`/admin-appointments/available-slots`, {
    params: {
      branchCode,
      date,
      duration,
    },
  });

  return response.data;
};

export const getAvailableSlotsForWeekThunk = createAsyncThunk(
  "appointment/getAvailableSlotsForWeek",
  async ({ branchCode, dates, duration }, { rejectWithValue }) => {
    try {
      console.log("📅 Fetching slots for dates:", dates);

      const promises = dates.map(async (date) => {
        const data = getAvailableSlots({
          branchCode,
          date,
          duration,
        });
        return data;
      });

      const results = await Promise.allSettled(promises);

      const slotsByDate = {};

      results.forEach((result, index) => {
        const date = dates[index];

        if (result.status === "fulfilled") {
          slotsByDate[date] = result.value.slots || [];
        } else {
          console.warn(`⚠️ Failed to fetch slots for ${date}:`, result.reason);
          slotsByDate[date] = [];
        }
      });

      console.log("✅ Fetched slots by date:", slotsByDate);

      return {
        slotsByDate,
        branchCode,
        duration,
      };
    } catch (error) {
      console.error("❌ Error fetching week slots:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch available slots"
      );
    }
  }
);

/**
 * Get available slots (admin - for rescheduling)
 */
export const getAvailableSlotsThunk = createAsyncThunk(
  "appointment/getAvailableSlots",
  async ({ branchCode, date, duration = 30 }, { rejectWithValue }) => {
    try {
      const formattedDate = new Date(date).toISOString().split("T")[0];

      const data = await getAvailableSlots({
        branchCode,
        date: formattedDate,
        duration,
      });
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch available slots"
      );
    }
  }
);

// ========================================
// ACTIONS FOR STATE MANAGEMENT
// ========================================

export const clearAppointmentError = createAction(
  "appointment/clearAppointmentError"
);
export const resetSelectedAppointment = createAction(
  "appointment/resetSelectedAppointment"
);
export const setAppointmentFilters = createAction(
  "appointment/setAppointmentFilters"
);
export const clearAppointmentFilters = createAction(
  "appointment/clearAppointmentFilters"
);
export const clearAvailableSlots = createAction(
  "appointment/clearAvailableSlots"
);
export const clearSearchResults = createAction(
  "appointment/clearSearchResults"
);

// 🆕 NEW: Additional state management actions
export const clearDashboardStats = createAction(
  "appointment/clearDashboardStats"
);
export const clearBookingDensity = createAction(
  "appointment/clearBookingDensity"
);
export const clearAvailableAdmins = createAction(
  "appointment/clearAvailableAdmins"
);
export const clearAdminSchedule = createAction(
  "appointment/clearAdminSchedule"
);
